<template>
  <div class="wh" :class="{ 'wh-chrome': showChrome, 'wh-bare': bare }" :style="frameStyle">
    <div v-if="showChrome" class="wh-head">
      <div class="wh-title">{{ placement.config.title }}</div>
      <div class="wh-tools">
        <button v-if="canDownload" type="button" class="wh-refresh" title="Download the data behind this widget" :disabled="loading" @click="downloadData">
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M8 2.5v8M4.5 7 8 10.5 11.5 7M3 13.5h10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
        <button v-if="canRefresh" type="button" class="wh-refresh" title="Refresh" :disabled="loading" @click="refresh">
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v3h-3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
      </div>
    </div>
    <div v-else-if="canRefresh" class="wh-float">
      <button v-if="canDownload" type="button" class="wh-refresh" title="Download the data behind this widget" :disabled="loading" @click="downloadData">
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M8 2.5v8M4.5 7 8 10.5 11.5 7M3 13.5h10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
      <button type="button" class="wh-refresh" title="Refresh" :disabled="loading" @click="refresh">
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v3h-3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
    </div>
    <FilterBar v-if="ownFilters.length && !drawsFilters" class="wh-filters" :filters="ownFilters" :values="local" @change="setLocal" />
    <div ref="body" class="wh-body" :class="{ 'wh-updating': loading && ready }" :style="{ padding: pad + 'px' }">
      <div v-if="loading && ready" class="wh-busy"><LoadingIndicator message="" /></div>
      <div class="wh-content" :style="contentStyle">
      <div v-if="error" class="wh-error">{{ error }}</div>
      <div v-else-if="loading && !ready" class="wh-center"><LoadingIndicator message="" /></div>
      <template v-else-if="widget">
        <component
          :is="stock"
          v-if="widget.kind === 'stock' && stock"
          :key="'stock:' + remounts"
          :inputs="inputs"
          :config="shown.config"
          :scope="shown.scope"
          :width="bodyWidth"
          :height="bodyHeight"
          @drill-entity="$emit('drill-entity', $event)"
          @select-patient="$emit('select-patient', $event)"
        />
        <InlineWidget
          v-else-if="widget.kind === 'd3'"
          :key="widget.version + ':' + reloads"
          :code="widget.definition.code || ''"
          :inputs="shownRows"
          :scope="shown.scope"
          :config="shown.config"
          :filters="shown.filters"
          :memory="memory"
          @set-filter="setLocal"
          @refresh="refresh"
          @empty="$emit('empty', $event)"
          @export="exportSheets"
          @drill-entity="$emit('drill-entity', $event)"
          @select-patient="$emit('select-patient', $event)"
        />
        <iframe
          v-else-if="widget.kind === 'html'"
          ref="htmlFrame"
          class="wh-frame"
          sandbox="allow-scripts"
          referrerpolicy="no-referrer"
          :srcdoc="htmlDocument"
          title="Widget"
        />
        <div v-else class="wh-error">Unknown widget kind {{ widget.kind }}.</div>
      </template>
      <div v-else class="wh-error">Widget {{ placement.widget }} is not available.</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InlineWidget from './InlineWidget.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import FilterBar from './FilterBar.vue';
import { localDefaults, pageScope } from './pageLayout';
import { stockComponent } from './registry';
import { TOKENS_CSS, FRAME_CSP, demoSafeInputs, jsonForScript } from './frameTokens';
import { exportToExcel } from '@/utils/exportArraytoXlsx';

const EXPORT_MAX_ROWS = 100000;
const MIN_HEIGHT = { chart: 320, sheet: 380, kpi: 110 };

const SHADOWS = {
  none: 'none',
  sm: '0 1px 3px rgba(26, 25, 24, 0.08)',
  md: '0 4px 14px rgba(26, 25, 24, 0.12)',
};

export default {
  name: 'WidgetHost',
  components: { InlineWidget, FilterBar, LoadingIndicator },
  inject: { pageRows: { default: null }, forgetSources: { default: null }, joinBatch: { default: null } },
  props: {
    placement: { type: Object, required: true },
    widget: { type: Object, default: null },
    pageId: { type: Number, default: null },
    sessionId: { type: String, default: '' },
    subject: { type: Object, default: null },
    filters: { type: Object, default: () => ({}) },
    preloaded: { type: Object, default: null },
    spec: { type: Object, default: () => ({}) },
    host: { type: Object, default: () => ({}) },
  },
  emits: ['drill-entity', 'select-patient', 'empty'],
  data() {
    return { inputs: {}, loading: false, ready: false, error: '', reloads: 0, remounts: 0, local: localDefaults(this.placement), box: { w: 0, h: 0 },
      shown: { scope: {}, filters: [], config: {} } };
  },
  computed: {
    bare() {
      return !!(this.widget && (this.widget.contract || {}).frame === 'own') && !this.showChrome;
    },
    ownFilters() {
      return this.placement.filters || [];
    },
    drawsFilters() {
      return !!(this.widget && this.widget.contract && this.widget.contract.draws_filters);
    },
    filterState() {
      return this.ownFilters.map((f) => ({ ...f, value: this.local[f.name] ?? '' }));
    },
    scope() {
      return pageScope(this.spec, this.placement, this.host, this.filters, this.local);
    },
    widgetScope() {
      const wanted = ((this.widget && this.widget.contract) || {}).params || [];
      return Object.fromEntries(wanted.map((p) => p.name).filter((n) => n in this.scope).map((n) => [n, this.scope[n]]));
    },
    stock() {
      return this.widget && this.widget.kind === 'stock' ? stockComponent(this.widget.component) : null;
    },
    showChrome() {
      return !!(this.placement.config && this.placement.config.title);
    },
    look() {
      return this.placement.style || {};
    },
    pad() {
      return Number(this.look.padding) || 0;
    },
    frameStyle() {
      const look = this.look;
      const style = {};
      if (look.background) style.background = look.background;
      if (look.border_color) style.borderColor = look.border_color;
      if (look.border_width != null) style.borderWidth = look.border_width + 'px';
      if (look.border_radius != null) style.borderRadius = look.border_radius + 'px';
      if (look.shadow) style.boxShadow = SHADOWS[look.shadow] || 'none';
      if (this.widget) {
        const min = this.widget.kind === 'html' ? 320 : MIN_HEIGHT[this.widget.component];
        if (min) style.minHeight = min + 'px';
      }
      return style;
    },
    bodyWidth() {
      return Math.max(0, this.box.w - 2 * this.pad);
    },
    bodyHeight() {
      return Math.max(0, this.box.h - 2 * this.pad);
    },
    sized() {
      return !!this.widget && (this.widget.kind === 'html' || (this.widget.kind === 'stock' && !!MIN_HEIGHT[this.widget.component]));
    },
    contentStyle() {
      return this.sized && this.bodyHeight ? { height: this.bodyHeight + 'px' } : {};
    },
    inputNames() {
      return Object.keys(this.placement.inputs || {});
    },
    sharedSources() {
      return [...new Set(Object.values(this.placement.inputs || {}).map((b) => b && b.source).filter(Boolean))];
    },
    canRefresh() {
      return !this.preloaded && !!this.widget;
    },
    canDownload() {
      return this.canRefresh && this.inputNames.length > 0;
    },
    shownRows() {
      const out = {};
      Object.keys(this.inputs || {}).forEach((name) => { out[name] = (this.inputs[name] || {}).rows || []; });
      return out;
    },
    inputRows() {
      const out = {};
      this.inputNames.forEach((name) => { out[name] = (this.inputs[name] || {}).rows || []; });
      return out;
    },
    htmlDocument() {
      const template = (this.widget && this.widget.definition && this.widget.definition.template) || '';
      const inputs = demoSafeInputs(this.inputRows);
      return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">
<style>${TOKENS_CSS}
html,body{margin:0;max-width:100%;overflow-x:hidden}
body{font:14px/1.45 -apple-system,system-ui,sans-serif;color:var(--body);background:var(--card)}</style>
</head><body>
<div id="root">${template}</div>
<script>
window.inputs = ${jsonForScript(inputs)};
window.config = ${jsonForScript(this.placement.config || {})};
window.exportData = function (spec) { parent.postMessage({ kaironExport: JSON.parse(JSON.stringify(spec || {})) }, '*'); };
document.dispatchEvent(new Event('kairon:ready'));
<\/script>
</body></html>`;
    },
  },
  watch: {
    scope: { deep: true, handler() { this.load(); } },
    'placement.inputs': { deep: true, handler() { this.load(); } },
  },
  created() {
    this.memory = {};
  },
  mounted() {
    window.addEventListener('message', this.onFrameMessage);
    this.load();
    this.observer = new ResizeObserver(() => {
      const el = this.$refs.body;
      if (el && (Math.abs(el.clientWidth - this.box.w) > 1 || Math.abs(el.clientHeight - this.box.h) > 1)) {
        this.box = { w: el.clientWidth, h: el.clientHeight };
      }
    });
    if (this.$refs.body) this.observer.observe(this.$refs.body);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onFrameMessage);
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    setLocal(name, value) {
      this.local = { ...this.local, [name]: value };
    },
    fetchInput(name) {
      const body = { placement: this.placement.id, input: name, params: this.filters, host: this.host, local: this.local };
      const request = () => (this.pageId
        ? axios.post(`/api/impact_analytics/agent-pages/${this.pageId}/rows/`, body)
        : this.subject && this.subject.type
          ? axios.post('/api/impact_analytics/agent-pages/default-rows/', { ...body, subject: this.subject.type, id: this.subject.id })
          : axios.post(`/api/impact_analytics/agent-sessions/${this.sessionId}/page-rows/`, {
          ...body, spec: this.spec,
          widgets: this.widget ? [{ slug: this.placement.widget, contract: this.widget.contract || {} }] : [],
        }));
      const shared = (this.placement.inputs[name] || {}).source;
      if (!shared || !this.pageRows) return request();
      return this.pageRows(`${shared}|${JSON.stringify(this.scope)}`, request);
    },
    onFrameMessage(event) {
      const frame = this.$refs.htmlFrame;
      if (!frame || event.source !== frame.contentWindow || !(event.data || {}).kaironExport) return;
      this.exportSheets(event.data.kaironExport);
    },
    exportSheets(spec) {
      const sheets = (Array.isArray(spec.sheets) ? spec.sheets : [])
        .filter((sheet) => sheet && Array.isArray(sheet.rows) && sheet.rows.every((r) => r && typeof r === 'object' && !Array.isArray(r)));
      const total = sheets.reduce((n, sheet) => n + sheet.rows.length, 0);
      if (!sheets.length || total > EXPORT_MAX_ROWS) return;
      const base = String(spec.filename || this.placement.id || 'export').replace(/[^\w .-]+/g, '').trim().slice(0, 80) || 'export';
      const names = sheets.map((sheet, i) => String(sheet.name || `Sheet ${i + 1}`).replace(/[\\/?*[\]:]/g, '').slice(0, 31) || `Sheet ${i + 1}`);
      exportToExcel(sheets.map((sheet) => sheet.rows), `${base}.xlsx`, { sheetNames: names });
    },
    downloadData() {
      const cell = (v) => (v !== null && typeof v === 'object' ? JSON.stringify(v) : v);
      const manifest = (this.widget && this.widget.contract && this.widget.contract.inputs) || [];
      const used = new Set();
      const sheetName = (want) => {
        const base = String(want).replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Sheet';
        let name = base;
        for (let n = 2; used.has(name); n += 1) name = `${base.slice(0, 27)} (${n})`;
        used.add(name);
        return name;
      };
      const parts = this.inputNames.map((name) => {
        const entry = manifest.find((i) => i.name === name) || {};
        return {
          sheet: sheetName(entry.display_name || name),
          description: entry.description || '',
          rows: ((this.inputs[name] || {}).rows || []).map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, cell(v)]))),
        };
      });
      const filters = Object.entries({ ...this.filters, ...this.local })
        .filter(([, v]) => v !== '' && v !== null && v !== undefined)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('; ');
      const about = parts.map((p) => ({ Sheet: p.sheet, Description: p.description, Rows: p.rows.length, Filters: filters }));
      const title = (this.placement.config && this.placement.config.title) || (this.widget && this.widget.name) || this.placement.id;
      this.exportSheets({ filename: title, sheets: [{ name: sheetName('About'), rows: about }, ...parts.map((p) => ({ name: p.sheet, rows: p.rows }))] });
    },
    refresh() {
      if (!this.inputNames.length) {
        this.remounts += 1;
        return;
      }
      if (this.sharedSources.length && this.forgetSources) this.forgetSources(this.sharedSources, this.scope);
      this.load(true);
    },
    inputKey(name) {
      let binding = this.placement.inputs[name] || {};
      if (binding.source) binding = (this.spec.sources || {})[binding.source] || {};
      const vars = new Set(['year', 'entity']);
      const walk = (v) => {
        if (!v || typeof v !== 'object') return;
        if (typeof v.var === 'string' && Object.keys(v).length === 1) vars.add(v.var);
        else Object.values(v).forEach(walk);
      };
      walk(binding.id);
      walk(binding.params);
      return JSON.stringify({ filters: this.filters, host: this.host, vars: [...vars].sort().map((v) => [v, this.scope[v] ?? null]) });
    },
    snapshot() {
      return { scope: { ...this.widgetScope }, filters: this.filterState.map((f) => ({ ...f })), config: { ...(this.placement.config || {}) } };
    },
    commit(inputs, error) {
      this.inputs = inputs;
      this.shown = this.snapshot();
      this.error = error;
      this.ready = true;
      this.reloads += 1;
    },
    async load(force) {
      const names = this.inputNames;
      if (this.preloaded) {
        const failed = names.find((name) => (this.preloaded[name] || {}).error);
        this.commit(this.preloaded, failed ? this.preloaded[failed].error : '');
        return;
      }
      if (!names.length) {
        this.commit({}, '');
        return;
      }
      const keys = Object.fromEntries(names.map((name) => [name, this.inputKey(name)]));
      const held = this.keys || {};
      const stale = names.filter((name) => force || held[name] !== keys[name] || !this.inputs[name] || this.inputs[name].error);
      if (!stale.length) {
        this.commit(this.inputs, this.error);
        return;
      }
      this.loading = true;
      const token = (this.loadToken = (this.loadToken || 0) + 1);
      const fetched = Promise.all(names.map((name) => (stale.includes(name)
        ? this.fetchInput(name)
          .then(({ data }) => [name, { rows: data.rows || [], columns: data.columns || [], chart: data.chart || null }])
          .catch((err) => [name, { rows: [], columns: [], error: err?.response?.data?.error || 'Rows failed to load.' }])
        : Promise.resolve([name, this.inputs[name]]))));
      try {
        const results = await (this.joinBatch ? this.joinBatch(fetched) : fetched);
        if (token !== this.loadToken) return;
        const inputs = {};
        results.forEach(([name, value]) => { inputs[name] = value; });
        this.keys = keys;
        const failed = results.find(([, v]) => v.error);
        this.commit(inputs, failed ? failed[1].error : '');
      } finally {
        if (token === this.loadToken) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.wh { position: relative; width: 100%; flex: 1 1 auto; box-sizing: border-box; display: flex; flex-direction: column; background: #fff; border: 1px solid #e6e4df; border-radius: 8px; overflow: hidden; }
.wh-head { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; height: 34px; border-bottom: 1px solid #f0efec; }
.wh-title { font-size: 12.5px; font-weight: 600; color: #1a1918; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wh-bare { border: 0; border-radius: 0; background: transparent; overflow: visible; }
.wh-body { flex: 1; min-height: 0; position: relative; box-sizing: border-box; display: flex; flex-direction: column; }
.wh-frame { width: 100%; height: 100%; border: 0; display: block; }
.wh-content { flex: 1 1 auto; min-width: 0; min-height: 0; display: flex; flex-direction: column; transition: opacity 0.15s; }
.wh-updating .wh-content { opacity: 0.55; }
.wh-busy { position: absolute; inset: 0; z-index: 3; display: flex; align-items: center; justify-content: center; pointer-events: none; }
.wh-center { flex: 1 1 auto; min-height: 160px; display: flex; align-items: center; justify-content: center; }
.wh-error { padding: 12px; font-size: 12px; color: #c23a28; }
.wh-tools { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.wh-refresh { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; padding: 0; border: 0; border-radius: 6px; background: transparent; color: #8b877f; cursor: pointer; opacity: 0; transition: opacity 0.15s, background 0.15s; }
.wh:hover .wh-refresh, .wh-refresh:focus-visible { opacity: 1; }
.wh-refresh:hover { background: #f0efec; color: #1a1918; }
.wh-refresh:disabled { cursor: default; }
.wh-float { position: absolute; top: 6px; right: 6px; z-index: 2; display: flex; gap: 4px; }
.wh-float .wh-refresh { background: rgba(255, 255, 255, 0.9); box-shadow: 0 1px 3px rgba(26, 25, 24, 0.12); }
</style>
